import { useJsApiLoader } from '@react-google-maps/api';
import { useEffect, useRef, useState } from 'react';
import { getGoogleMapsJsApiLoaderOptions } from 'react-components/maps/googleMapsJsApiLoader.config';
import {
  getPlaceAutocompleteElementConstructor,
  type GmpSelectAutocompleteEvent,
  type PlaceAutocompleteElement,
} from '../googlePlacesNewAutocomplete.types';

const MEMBER_LOCATION_RESTRICTION_RADIUS_METERS = 80000;
const METERS_PER_DEGREE_LATITUDE = 111320;
const PLACES_SEARCH_COMPONENT_RESTRICTIONS = { country: ['us'] };

export interface UsePlacesAutocompleteParams {
  container: HTMLElement | null;
  /** Invoked when the user selects a prediction (after widget `gmp-select`). */
  onGmpSelect: (event: GmpSelectAutocompleteEvent) => void | Promise<void>;
  /** Member address used to restrict predictions by proximity. */
  locationBiasAddress?: string;
  placeholder?: string;
}

export interface UsePlacesAutocompleteResult {
  isReady: boolean;
  loadError: Error | undefined;
  widgetUnavailable: boolean;
}

/**
 * Mounts Google `PlaceAutocompleteElement` into `container`.
 *
 * @param params - Host element and async `gmp-select` handler.
 * @returns Ready state and error flags for UI.
 */
export function usePlacesAutocomplete({
  container,
  locationBiasAddress,
  onGmpSelect,
  placeholder = 'Search for resource',
}: UsePlacesAutocompleteParams): UsePlacesAutocompleteResult {
  const { isLoaded, loadError } = useJsApiLoader(getGoogleMapsJsApiLoaderOptions());
  const [widgetUnavailable, setWidgetUnavailable] = useState(false);
  const onGmpSelectRef = useRef(onGmpSelect);
  onGmpSelectRef.current = onGmpSelect;

  useEffect(() => {
    if (!isLoaded || loadError || !container) {
      return;
    }

    const PlaceAutocompleteElement = getPlaceAutocompleteElementConstructor();
    if (!PlaceAutocompleteElement) {
      setWidgetUnavailable(true);
      return;
    }
    setWidgetUnavailable(false);

    let isActive = true;
    let placeAutocomplete: PlaceAutocompleteElement | null = null;

    const handleGmpSelect = (event: Event) => {
      void onGmpSelectRef.current(event as GmpSelectAutocompleteEvent);
    };

    void (async () => {
      const locationRestriction =
        await getLocationRestrictionFromAddress(locationBiasAddress);
      if (!isActive) {
        return;
      }

      placeAutocomplete = new PlaceAutocompleteElement({
        componentRestrictions: PLACES_SEARCH_COMPONENT_RESTRICTIONS,
        locationRestriction,
        placeholder,
      });
      placeAutocomplete.style.width = '100%';
      placeAutocomplete.style.boxSizing = 'border-box';
      placeAutocomplete.addEventListener('gmp-select', handleGmpSelect);
      container.appendChild(placeAutocomplete);
    })();

    return () => {
      isActive = false;
      placeAutocomplete?.removeEventListener('gmp-select', handleGmpSelect);
      placeAutocomplete?.remove();
      container.replaceChildren();
    };
  }, [container, isLoaded, loadError, locationBiasAddress, placeholder]);

  return {
    isReady: isLoaded && !loadError && !widgetUnavailable,
    loadError,
    widgetUnavailable,
  };
}

async function getLocationRestrictionFromAddress(
  address: string | undefined
): Promise<google.maps.LatLngBoundsLiteral | null> {
  const trimmedAddress = address?.trim();
  if (!trimmedAddress) {
    return getBrowserLocationRestriction();
  }

  try {
    const { results } = await new google.maps.Geocoder().geocode({
      address: trimmedAddress,
    });
    const location = results[0]?.geometry.location;
    if (!location) {
      return null;
    }

    return getBoundsAroundLocation(location);
  } catch {
    return getBrowserLocationRestriction();
  }
}

function getBoundsAroundLocation(
  location: google.maps.LatLng | google.maps.LatLngLiteral
): google.maps.LatLngBoundsLiteral {
  const lat = typeof location.lat === 'function' ? location.lat() : location.lat;
  const lng = typeof location.lng === 'function' ? location.lng() : location.lng;
  const latDelta =
    MEMBER_LOCATION_RESTRICTION_RADIUS_METERS / METERS_PER_DEGREE_LATITUDE;
  const lngDelta =
    MEMBER_LOCATION_RESTRICTION_RADIUS_METERS /
    (METERS_PER_DEGREE_LATITUDE * Math.cos((lat * Math.PI) / 180));

  return {
    east: lng + lngDelta,
    north: lat + latDelta,
    south: lat - latDelta,
    west: lng - lngDelta,
  };
}

function getBrowserLocationRestriction(): Promise<google.maps.LatLngBoundsLiteral | null> {
  if (!navigator.geolocation) {
    return Promise.resolve(null);
  }

  return new Promise(resolve => {
    navigator.geolocation.getCurrentPosition(
      position => {
        resolve(
          getBoundsAroundLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          })
        );
      },
      () => resolve(null),
      { maximumAge: 300000, timeout: 3000 }
    );
  });
}
